import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Divider,
  CircularProgress,
  InputAdornment,
  Slide,
} from "@mui/material";
import { Save as SaveIcon, Add as AddIcon } from "@mui/icons-material";
import { Country, State, City } from "country-state-city";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const FieldLabel = ({ children, required = false, width = "140px" }) => (
  <Box
    sx={{
      minWidth: width,
      maxWidth: width,
      display: "flex",
      alignItems: "center",
      fontSize: "13px",
      fontWeight: 400,
      color: "#4B5563",
      pr: 2,
    }}
  >
    {children}
    {required && <span style={{ color: "#E85858", marginLeft: "2px" }}>*</span>}
  </Box>
);

const FormField = ({ children, sx = {} }) => (
  <Box
    sx={{
      mb: 1.5,
      display: "flex",
      alignItems: "flex-start",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const CreateInstituteModal = ({
  open,
  onClose,
  onSuccess,
  instituteTypes,
  onCreateNewType,
}) => {
  const [instituteName, setInstituteName] = useState("");
  const [instituteType, setInstituteType] = useState(null);
  const [universityName, setUniversityName] = useState("");
  const [countries] = useState(Country.getAllCountries());
  const [billingStates, setBillingStates] = useState([]);
  const [billingCities, setBillingCities] = useState([]);
  const [billingAttention, setBillingAttention] = useState("");
  const [billingCountry, setBillingCountry] = useState(null);
  const [billingAddressLine1, setBillingAddressLine1] = useState("");
  const [billingAddressLine2, setBillingAddressLine2] = useState("");
  const [billingCity, setBillingCity] = useState(null);
  const [billingState, setBillingState] = useState(null);
  const [billingPincode, setBillingPincode] = useState("");
  const [billingPhone, setBillingPhone] = useState("");
  const [billingFax, setBillingFax] = useState("");
  const [website, setWebsite] = useState("");
  const [gstinNo, setGstinNo] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (billingCountry) {
      const states = State.getStatesOfCountry(billingCountry.isoCode);
      setBillingStates(states);
    } else {
      setBillingStates([]);
      setBillingState(null);
    }
  }, [billingCountry]);

  useEffect(() => {
    if (billingCountry && billingState) {
      const cities = City.getCitiesOfState(
        billingCountry.isoCode,
        billingState.isoCode
      );
      setBillingCities(cities);
    } else {
      setBillingCities([]);
    }
  }, [billingState, billingCountry]);

  const handleCityChange = (event, newValue) => {
    if (!newValue) {
      setBillingCity(null);
      return;
    }
    if (typeof newValue === "string") {
      setBillingCity(newValue);
      return;
    }
    setBillingCity(newValue);
  };

  const getPhoneCode = () => {
    if (billingCountry && billingCountry.phonecode) {
      return `+${billingCountry.phonecode}`;
    }
    return "";
  };

  const isFormValid = () => {
    return (
      instituteName.trim() !== "" &&
      billingCountry !== null &&
      billingState !== null &&
      billingAddressLine1.trim() !== "" &&
      billingCity !== null &&
      billingCity !== "" &&
      billingPincode.trim() !== ""
    );
  };

  const handleSubmit = async () => {
    setAttemptedSubmit(true);

    if (!isFormValid()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const phoneCode = getPhoneCode();
      const fullPhone = billingPhone ? `${phoneCode}-${billingPhone}` : "";
      const fullFax = billingFax ? `${phoneCode}-${billingFax}` : "";

      const payload = {
        institute_name: instituteName,
        institute_type: instituteType || "",
        university_name: universityName || instituteName,
        billing_address: {
          attention: billingAttention,
          address_line1: billingAddressLine1,
          address_line2: billingAddressLine2,
          city:
            typeof billingCity === "string" ? billingCity : billingCity?.name,
          state_name: billingState?.name,
          country_name: billingCountry?.name,
          pincode: billingPincode,
          phone: fullPhone,
          fax: fullFax,
        },
        website: website,
        gstin_no: gstinNo,
      };

      const response = await axios.post(
        `${BASE_URL}/accounts/institute`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      // Call success callback with the new institute data
      if (onSuccess) {
        onSuccess(response.data);
      }

      handleReset();
      onClose();
    } catch (error) {
      // console.error("Error creating institute:", error);
      alert(
        error.response?.data?.detail ||
          "An error occurred while creating the institute."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setInstituteName("");
    setInstituteType(null);
    setUniversityName("");
    setBillingAttention("");
    setBillingCountry(null);
    setBillingAddressLine1("");
    setBillingAddressLine2("");
    setBillingCity(null);
    setBillingState(null);
    setBillingPincode("");
    setBillingPhone("");
    setBillingFax("");
    setWebsite("");
    setGstinNo("");
    setAttemptedSubmit(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
    >
      <DialogTitle sx={{ fontSize: "16px", fontWeight: 600, px: 3, py: 2 }}>
        Create New Institute
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ mb: 2 }}>
          <FormField>
            <FieldLabel required>Institute Name</FieldLabel>
            <TextField
              value={instituteName}
              onChange={(e) => setInstituteName(e.target.value)}
              placeholder="Enter institute name"
              size="small"
              sx={{ flex: 1, maxWidth: "350px" }}
              error={attemptedSubmit && !instituteName.trim()}
              helperText={
                attemptedSubmit && !instituteName.trim()
                  ? "Institute name is required"
                  : ""
              }
            />
          </FormField>

          <FormField>
            <FieldLabel>Institute Type</FieldLabel>
            <Autocomplete
              value={instituteType}
              onChange={(event, newValue) => {
                if (newValue === "Create New") {
                  onCreateNewType();
                } else {
                  setInstituteType(newValue);
                }
              }}
              options={[...instituteTypes, "Create New"]}
              sx={{ flex: 1, maxWidth: "350px" }}
              renderOption={(props, option) => {
                if (option === "Create New") {
                  return null;
                }
                return (
                  <li {...props} style={{ fontSize: "13px" }}>
                    {option}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select institute type"
                  size="small"
                />
              )}
            />
          </FormField>

          {/* <FormField>
            <FieldLabel>University Name</FieldLabel>
            <TextField
              value={universityName}
              onChange={(e) => setUniversityName(e.target.value)}
              placeholder="Enter university name (optional)"
              size="small"
              sx={{ flex: 1, maxWidth: "350px" }}
            />
          </FormField> */}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            mb: 2,
            color: "#1F1F1F",
          }}
        >
          Billing Address
        </Typography>

        <FormField>
          <FieldLabel>Attention</FieldLabel>
          <TextField
            value={billingAttention}
            onChange={(e) => setBillingAttention(e.target.value)}
            size="small"
            sx={{ flex: 1, maxWidth: "300px" }}
          />
        </FormField>

        <FormField>
          <FieldLabel required>Country/Region</FieldLabel>
          <Autocomplete
            value={billingCountry}
            onChange={(event, newValue) => {
              setBillingCountry(newValue);
              if (!newValue) {
                setBillingState(null);
                setBillingCity(null);
                setBillingPhone("");
                setBillingFax("");
              }
            }}
            options={countries}
            getOptionLabel={(option) => option.name || ""}
            renderOption={(props, option) => (
              <li {...props} style={{ fontSize: "13px" }}>
                {option.flag} {option.name}
              </li>
            )}
            sx={{ flex: 1, maxWidth: "300px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select country"
                size="small"
                error={attemptedSubmit && !billingCountry}
                helperText={
                  attemptedSubmit && !billingCountry
                    ? "Country is required"
                    : ""
                }
              />
            )}
          />
        </FormField>

        <FormField>
          <FieldLabel required>State</FieldLabel>
          <Autocomplete
            value={billingState}
            onChange={(event, newValue) => {
              setBillingState(newValue);
              if (!newValue) {
                setBillingCity(null);
              }
            }}
            options={billingStates}
            getOptionLabel={(option) => option.name || ""}
            disabled={!billingCountry}
            renderOption={(props, option) => (
              <li {...props} style={{ fontSize: "13px" }}>
                {option.name}
              </li>
            )}
            sx={{ flex: 1, maxWidth: "300px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  billingCountry ? "Select state" : "Select country first"
                }
                size="small"
                error={attemptedSubmit && !billingState}
                helperText={
                  attemptedSubmit && !billingState ? "State is required" : ""
                }
              />
            )}
          />
        </FormField>

        <FormField>
          <FieldLabel required>City</FieldLabel>
          <Autocomplete
            freeSolo
            value={billingCity}
            onChange={handleCityChange}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") {
                setBillingCity(newInputValue);
              }
            }}
            options={billingCities}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option?.name || "";
            }}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options.slice(0, 50);
              return options
                .filter((option) =>
                  option.name.toLowerCase().includes(inputValue.toLowerCase())
                )
                .slice(0, 50);
            }}
            disabled={!billingState}
            sx={{ flex: 1, maxWidth: "300px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  billingState ? "Enter or select city" : "Select state first"
                }
                size="small"
                error={attemptedSubmit && !billingCity}
                helperText={
                  attemptedSubmit && !billingCity ? "City is required" : ""
                }
              />
            )}
            renderOption={(props, option) => (
              <li {...props} style={{ fontSize: "13px" }}>
                {option.name}
              </li>
            )}
          />
        </FormField>

        <FormField>
          <FieldLabel required>Address</FieldLabel>
          <TextField
            value={billingAddressLine1}
            onChange={(e) => setBillingAddressLine1(e.target.value)}
            placeholder="Street 1"
            multiline
            rows={2}
            sx={{ flex: 1, maxWidth: "300px" }}
            error={attemptedSubmit && !billingAddressLine1.trim()}
            helperText={
              attemptedSubmit && !billingAddressLine1.trim()
                ? "Address is required"
                : ""
            }
          />
        </FormField>

        <FormField>
          <FieldLabel width="140px"></FieldLabel>
          <TextField
            value={billingAddressLine2}
            onChange={(e) => setBillingAddressLine2(e.target.value)}
            placeholder="Street 2"
            multiline
            rows={2}
            sx={{ flex: 1, maxWidth: "300px" }}
          />
        </FormField>

        <FormField>
          <FieldLabel required>Pin Code</FieldLabel>
          <TextField
            value={billingPincode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              if (value.length <= 10) {
                setBillingPincode(value);
              }
            }}
            size="small"
            inputProps={{ maxLength: 10 }}
            sx={{ flex: 1, maxWidth: "300px" }}
            error={attemptedSubmit && !billingPincode.trim()}
            helperText={
              attemptedSubmit && !billingPincode.trim()
                ? "Pin code is required"
                : ""
            }
          />
        </FormField>

        <FormField>
          <FieldLabel>Phone</FieldLabel>
          <TextField
            value={billingPhone}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              setBillingPhone(value);
            }}
            placeholder={
              billingCountry ? "Enter phone number" : "Select country first"
            }
            size="small"
            disabled={!billingCountry}
            sx={{ flex: 1, maxWidth: "300px" }}
            InputProps={{
              startAdornment: billingCountry && (
                <InputAdornment position="start">
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#6B7280",
                      fontWeight: 500,
                    }}
                  >
                    {getPhoneCode()}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </FormField>

        <FormField>
          <FieldLabel>Fax Number</FieldLabel>
          <TextField
            value={billingFax}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              setBillingFax(value);
            }}
            placeholder={
              billingCountry ? "Enter fax number" : "Select country first"
            }
            size="small"
            disabled={!billingCountry}
            sx={{ flex: 1, maxWidth: "300px" }}
            InputProps={{
              startAdornment: billingCountry && (
                <InputAdornment position="start">
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#6B7280",
                      fontWeight: 500,
                    }}
                  >
                    {getPhoneCode()}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </FormField>

        <Divider sx={{ my: 2 }} />

        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            mb: 2,
            color: "#1F1F1F",
          }}
        >
          Other Details
        </Typography>

        <FormField>
          <FieldLabel>Institute Website</FieldLabel>
          <TextField
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            type="url"
            size="small"
            sx={{ flex: 1, maxWidth: "300px" }}
          />
        </FormField>

        <FormField>
          <FieldLabel>GSTIN No.</FieldLabel>
          <TextField
            value={gstinNo}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              if (value.length <= 15) {
                setGstinNo(value);
              }
            }}
            size="small"
            inputProps={{ maxLength: 15 }}
            sx={{ flex: 1, maxWidth: "300px" }}
          />
        </FormField>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            color: "#6B7280",
            textTransform: "none",
            fontSize: "13px",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          sx={{
            backgroundColor: "#3B82F6",
            "&:hover": { backgroundColor: "#2563EB" },
            textTransform: "none",
            fontSize: "13px",
          }}
        >
          {isSubmitting ? "Creating..." : "Create Institute"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInstituteModal;
